import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  Alert,
  Chip,
  InputAdornment,
} from '@mui/material';
import { Add, Edit, Delete, RestoreFromTrash, Search } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Tenant } from '../types';
import { tenantService } from '../services/tenantService';
import { getErrorMessage } from '../utils/errors';
import ConfirmDialog from '../components/ConfirmDialog';

const schema = yup.object({
  nome: yup.string().required('Nome é obrigatório'),
  cnpj: yup
    .string()
    .required('CNPJ é obrigatório')
    .matches(/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/, 'CNPJ inválido (XX.XXX.XXX/XXXX-XX)'),
}).required();

const formatCnpj = (value: string): string => {
  const d = value.replace(/\D/g, '').slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

const Tenants: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [open, setOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
  const [confirmReativar, setConfirmReativar] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
  const [filtro, setFiltro] = useState('');

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
  });

  const cnpjValue = watch('cnpj');

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      const data = await tenantService.listar();
      setTenants(data);
    } catch (error) {
      console.error('Erro ao carregar organizações', error);
    }
  };

  const handleOpen = (tenant?: Tenant) => {
    if (tenant) {
      setEditingTenant(tenant);
      reset(tenant);
    } else {
      setEditingTenant(null);
      reset({ nome: '', cnpj: '' } as any);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingTenant(null);
    reset();
    setErro('');
  };

  const handleCnpjChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValue('cnpj', formatCnpj(e.target.value));
  };

  const onSubmit = async (data: any) => {
    setSalvando(true);
    try {
      const payload = { ...data, cnpj: data.cnpj.replace(/\D/g, '') };
      if (editingTenant?.id) {
        await tenantService.atualizar(editingTenant.id, payload);
      } else {
        await tenantService.cadastrar(payload);
      }
      loadTenants();
      handleClose();
    } catch (error) {
      setErro(getErrorMessage(error));
    } finally {
      setSalvando(false);
    }
  };

  const handleDelete = (id: number) => {
    setConfirmDelete({ open: true, id });
  };

  const confirmarDelete = async () => {
    if (confirmDelete.id === null) return;
    try {
      await tenantService.deletar(confirmDelete.id);
      loadTenants();
    } catch (error) {
      console.error('Erro ao desativar organização', error);
      setErro(getErrorMessage(error));
    } finally {
      setConfirmDelete({ open: false, id: null });
    }
  };

  const handleReativar = (id: number) => {
    setConfirmReativar({ open: true, id });
  };

  const confirmarReativar = async () => {
    if (confirmReativar.id === null) return;
    try {
      await tenantService.reativar(confirmReativar.id);
      loadTenants();
    } catch (error) {
      console.error('Erro ao reativar organização', error);
      setErro(getErrorMessage(error));
    } finally {
      setConfirmReativar({ open: false, id: null });
    }
  };

  const termo = filtro.trim().toLowerCase();
  const tenantsFiltrados = termo
    ? tenants.filter((item) => Object.values(item).filter(Boolean).some((campo) => String(campo).toLowerCase().includes(termo)))
    : tenants;

  return (
    <Container disableGutters maxWidth={false}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Organizações</Typography>
        <Box display="flex" gap={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Pesquisar"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ minWidth: 260 }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
          >
            Nova Organização
          </Button>
        </Box>
      </Box>

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 56 }}>#</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>CNPJ</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenantsFiltrados.map((tenant, indice) => (
              <TableRow key={tenant.id}>
                <TableCell>{indice + 1}</TableCell>
                <TableCell>{tenant.nome}</TableCell>
                <TableCell>{tenant.cnpj}</TableCell>
                <TableCell>
                  {tenant.ativo === false ? (
                    <Chip label="Inativo" size="small" color="error" />
                  ) : (
                    <Chip label="Ativo" size="small" color="success" />
                  )}
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(tenant)}>
                    <Edit />
                  </IconButton>
                  {tenant.ativo === false ? (
                    <IconButton size="small" color="success" onClick={() => tenant.id && handleReativar(tenant.id)}>
                      <RestoreFromTrash />
                    </IconButton>
                  ) : (
                    <IconButton size="small" color="error" onClick={() => tenant.id && handleDelete(tenant.id)}>
                      <Delete />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTenant ? 'Editar Organização' : 'Nova Organização'}</DialogTitle>
        <DialogContent>
          {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
          <form id="tenant-form" onSubmit={handleSubmit(onSubmit as any)}>
            <TextField
              fullWidth
              label="Nome"
              {...register('nome')}
              error={!!errors.nome}
              helperText={errors.nome?.message}
              margin="normal"
            />

            <TextField
              fullWidth
              label="CNPJ"
              value={cnpjValue || ''}
              onChange={handleCnpjChange}
              error={!!errors.cnpj}
              helperText={errors.cnpj?.message}
              placeholder="00.000.000/0000-00"
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button type="submit" form="tenant-form" variant="contained" disabled={salvando}>
            {salvando ? 'Salvando...' : (editingTenant ? 'Atualizar' : 'Cadastrar')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete.open}
        title="Desativar Organização"
        message="Tem certeza que deseja desativar esta organização?"
        confirmText="Desativar"
        severity="error"
        onConfirm={confirmarDelete}
        onCancel={() => setConfirmDelete({ open: false, id: null })}
      />

      <ConfirmDialog
        open={confirmReativar.open}
        title="Reativar Organização"
        message="Tem certeza que deseja reativar esta organização?"
        confirmText="Reativar"
        severity="info"
        onConfirm={confirmarReativar}
        onCancel={() => setConfirmReativar({ open: false, id: null })}
      />
    </Container>
  );
};

export default Tenants;
